'use client'

import { usePathname, useSearchParams } from 'next/navigation'
import { useEffect, useState } from 'react'

const base = process.env.NEXT_PUBLIC_BASE_PATH ?? ''

// Thin bar at the top of the window while the next page is rendered on the server (QA 08/10: clicking a link gave no
// feedback for up to a few seconds). No loading.tsx on purpose: streamed pages would answer 200 instead of 404.
export function NavigationProgress() {
  const pathname = usePathname()
  const search = useSearchParams()?.toString()
  const [active, setActive] = useState(false)

  useEffect(() => setActive(false), [pathname, search])

  useEffect(() => {
    if (!active) return
    const timer = window.setTimeout(() => setActive(false), 10_000)
    return () => window.clearTimeout(timer)
  }, [active])

  useEffect(() => {
    const isPageChange = (url: URL) =>
      url.origin === location.origin &&
      !url.pathname.startsWith(`${base}/admin`) &&
      !url.pathname.startsWith(`${base}/api`) &&
      (url.pathname !== location.pathname || url.search !== location.search)
    const onClick = (event: MouseEvent) => {
      // Capture phase, before next/link calls preventDefault() to route on the client.
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
      const link = (event.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null
      if (!link || (link.target && link.target !== '_self') || link.hasAttribute('download')) return
      if (isPageChange(new URL(link.href, location.href))) setActive(true)
    }
    // GET forms (training filters, searches) navigate too.
    const onSubmit = (event: SubmitEvent) => {
      const form = event.target as HTMLFormElement
      if (event.defaultPrevented || form.method.toLowerCase() !== 'get') return
      setActive(true)
    }
    document.addEventListener('click', onClick, true)
    document.addEventListener('submit', onSubmit)
    return () => {
      document.removeEventListener('click', onClick, true)
      document.removeEventListener('submit', onSubmit)
    }
  }, [])

  return <div className="nav-progress" data-active={active ? 'true' : 'false'} aria-hidden="true" data-testid="nav-progress" />
}
